import { useContext, useEffect, useRef, useState } from 'react';

import { CameraStateDispatchContext } from 'context/state/camera';
import { WebRTCContext } from 'context/webRTC';
import { dispatchEvent, subscribe, unsubscribe } from 'modules/globalEvents';
import { createMediaStreamUsingTransceivers, toggleMicrophoneTrack, isValidPeerConnection } from 'modules/webRTC';

import { AppDeviceLeaf } from 'types/common/leaf';

interface WebRTCParams {
  leaf: AppDeviceLeaf;
}

interface WebRTCOptions {
  autoConnect: boolean;
  autoReconnect: boolean;
}

interface WebRTCReturn {
  connect: () => Promise<void>;
  remoteStream: MediaStream | undefined;
  rtcConnection: RTCPeerConnection | undefined;
  rtcConnectionState: RTCPeerConnectionState;
  rtcLoading: boolean;
  rtcError: Error | undefined;
}

const DEFAULT_WEBRTC_OPTIONS: WebRTCOptions = {
  autoConnect: true,
  autoReconnect: true,
};

export const useWebRTC = (
  { leaf }: WebRTCParams,
  options: Partial<WebRTCOptions> = DEFAULT_WEBRTC_OPTIONS,
): WebRTCReturn => {
  const { autoConnect, autoReconnect } = options;

  const { peerConnections, connect, close } = useContext(WebRTCContext);
  const peerConnection = peerConnections.get(leaf.id);

  const { state: leafState } = leaf;
  const powerState = leafState?.power?.power;
  const onlineState = leafState?.online;
  const isCameraAvailable = onlineState?.online && (!leafState?.power || powerState === 'on');

  const initLoadingState =
    (autoConnect &&
      isCameraAvailable &&
      ['new', 'connecting'].includes(peerConnection?.connectionState as string) &&
      !peerConnection?.error) ||
    false;

  const [remoteStream, setRemoteStream] = useState<MediaStream | undefined>(peerConnection?.mediaStream);
  const [rtcConnection, setRtcConnection] = useState<RTCPeerConnection | undefined>(peerConnection);
  const [rtcConnectionState, setRtcConnectionState] = useState<WebRTCReturn['rtcConnectionState']>(
    peerConnection?.connectionState || 'new',
  );
  const [rtcLoading, setRtcLoading] = useState<boolean>(initLoadingState);
  const [rtcError, setRtcError] = useState<Error | undefined>(peerConnection?.error);

  const rtcListeners = useRef<Map<string, EventListener>>(new Map());

  // Previous states
  const previousLeafState = useRef(leafState);

  const dispatch = useContext(CameraStateDispatchContext);

  // Subscribe to global connection events
  useEffect(() => {
    // This covers the case where a new peer connection has to be created
    const newConnectionListener = (leafId: AppDeviceLeaf['id']) => {
      if (leafId === leaf.id) {
        const pc = peerConnections.get(leafId);
        if (pc) {
          setRtcConnection(pc);
          subscribeToRTCEvents(pc);
        }
      }
    };
    peerConnections.on('newconnection', newConnectionListener);

    return () => {
      peerConnections.off('newconnection', newConnectionListener);
    };
  }, []);

  // This effect handles when the streaming component is rendered on the UI
  useEffect(() => {
    if (!rtcConnection && isCameraAvailable && autoConnect) {
      connect({ leaf, peerConnection: rtcConnection, trigger: 'auto' });
    }

    if (rtcConnection) {
      if (rtcConnection.error) {
        setRtcError(rtcConnection.error);
      }

      setRtcConnectionState(rtcConnection.connectionState);
      subscribeToRTCEvents(rtcConnection);
    }

    return () => {
      if (rtcConnection) {
        unsubscribeFromRTCEvents(rtcConnection);
      }
    };
  }, [rtcConnection, leaf.id]);

  // // This effect handles when the remote stream is changed
  useEffect(() => {
    if (rtcConnection && !remoteStream) {
      if (rtcConnection?.mediaStream) {
        setRemoteStream(rtcConnection?.mediaStream);
      } else {
        const stream = createMediaStreamUsingTransceivers(rtcConnection.getTransceivers());
        setRemoteStream(stream);
      }
    }
  }, [rtcConnection, rtcConnection?.mediaStream, remoteStream, leaf.id]);

  // Handle camera availability changes (online/power state) and auto initiate connection
  useEffect(() => {
    const previousOnlineState = previousLeafState.current?.online;
    const previousPowerState = previousLeafState.current?.power?.power;

    const onlineChanged = previousOnlineState?.updated !== onlineState?.updated;
    const powerChanged = previousPowerState !== powerState;

    // Ignore older/stale online state updates to prevent race conditions
    const isNewerOnlineState =
      !previousOnlineState?.updated || (onlineState?.updated && onlineState.updated > previousOnlineState.updated);

    // Only act on actual state changes and newer online states
    if ((onlineChanged || powerChanged) && (powerChanged || isNewerOnlineState)) {
      // Auto-reconnect if camera becomes available
      if (isCameraAvailable && onlineState?.online && autoReconnect) {
        // Check if we already have a valid connection
        if (!isValidPeerConnection(peerConnection)) {
          connect({ leaf, peerConnection, trigger: 'auto' });
        }
      }
      // Reset connection if camera becomes unavailable
      else if (!isCameraAvailable) {
        close({ leafId: leaf.id });
      }
    }

    // Update ref AFTER processing to prevent race conditions
    previousLeafState.current = leafState;
  }, [leaf.id, onlineState?.updated, powerState]);

  // Set the initial mic state based on sender tracks
  useEffect(() => {
    if (rtcConnection) {
      rtcConnection?.getTransceivers().forEach((transceiver) => {
        if (transceiver.direction === 'sendrecv' && transceiver.sender.track) {
          dispatch({ type: 'microphone', newValue: true });
        }
      });

      subscribe(leaf.id, 'TOGGLE_MICROPHONE', async ({ detail }) => {
        if (rtcConnection?.connectionState === 'connected') {
          try {
            if (detail.leafId === leaf.id) {
              await toggleMicrophoneTrack(detail.data.newValue ? 'on' : 'off', rtcConnection);

              // Enable sound if the microphone is enabled
              if (detail.data.newValue) {
                dispatchEvent({
                  leafId: leaf.id,
                  event: 'TOGGLE_SOUND',
                  data: { newValue: true },
                });
              }

              dispatch({ type: 'microphone', newValue: detail.data.newValue });
            } else {
              await toggleMicrophoneTrack('off', rtcConnection, false);
              dispatch({ type: 'microphone', newValue: false });
            }
          } catch {
            dispatch({ type: 'microphone', newValue: false });
          }
        }
      });
    }

    return () => {
      unsubscribe(leaf.id, 'TOGGLE_MICROPHONE');
      try {
        if (rtcConnection?.connectionState === 'connected') {
          (async () => toggleMicrophoneTrack('off', rtcConnection, true))();
        }
        dispatch({ type: 'microphone', newValue: false });
      } catch {
        dispatch({ type: 'microphone', newValue: false });
      }
    };
  }, [rtcConnection, leaf.id]);

  const handleError = (event: Event) => {
    const error = (event as CustomEvent).detail as Error;

    setRtcError(error);
    setRtcLoading(false);
  };

  const connectFn = async () => {
    await connect({ leaf, peerConnection, trigger: 'manual' });
  };

  const handleConnectionStateChange = (event: Event) => {
    const connectionFromEvent = event.target as RTCPeerConnection;
    const { connectionState } = connectionFromEvent;

    // Set connection state
    setRtcConnectionState(connectionState);

    // Set loading state
    if (['new', 'connecting'].includes(connectionState)) {
      setRtcLoading(true);
    } else if (!connectionFromEvent.negotiationInProgress && !connectionFromEvent.offerInProgress) {
      setRtcLoading(false);
    }
  };

  const handleOnTrack = (event: RTCTrackEvent) => {
    const [stream] = event.streams;
    setRemoteStream(stream);
  };

  const handleOnLoadingChange = (event: Event) => {
    const { loading } = (event as CustomEvent).detail as { loading: boolean };
    setRtcLoading(loading);
  };

  interface SubscribeToRTCEventProps {
    pc: RTCPeerConnection;
    event: string;
    listener: EventListener;
  }

  const handleOnClose = () => {
    setRtcConnectionState('closed');
  };

  const subscribeToRTCEvent = ({ pc, event, listener }: SubscribeToRTCEventProps) => {
    if (rtcListeners.current?.get(event)) {
      unsubscribeFromRTCEvent({ pc, event });
    }

    pc.addEventListener(event, listener);
    rtcListeners.current.set(event, listener);
  };

  interface UnsubscribeFromRTCEventProps {
    pc: RTCPeerConnection;
    event: string;
  }
  const unsubscribeFromRTCEvent = ({ pc, event }: UnsubscribeFromRTCEventProps) => {
    const listener = rtcListeners.current?.get(event);
    if (listener) {
      rtcListeners.current.delete(event);
      pc.removeEventListener(event, listener as EventListener);
    }
  };

  const subscribeToRTCEvents = (pc: RTCPeerConnection) => {
    subscribeToRTCEvent({ pc, event: 'error', listener: handleError });
    subscribeToRTCEvent({ pc, event: 'loadingchange', listener: handleOnLoadingChange });
    subscribeToRTCEvent({ pc, event: 'close', listener: handleOnClose });
    subscribeToRTCEvent({
      pc,
      event: 'connectionstatechange',
      listener: handleConnectionStateChange,
    });
    subscribeToRTCEvent({ pc, event: 'track', listener: handleOnTrack as EventListener });
  };

  const unsubscribeFromRTCEvents = (pc: RTCPeerConnection) => {
    rtcListeners.current.forEach((_listener, event) => {
      unsubscribeFromRTCEvent({ pc, event });
    });
  };

  return { connect: connectFn, rtcConnection, remoteStream, rtcConnectionState, rtcLoading, rtcError };
};
